import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import { Alert, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useAppSelector } from "../../app/hooks";
import { useAllInstitutions } from "../Institutions/institutionUtils";
import {
  selectCurrentUser,
  selectUserRoles
} from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import {
  ProgramFormErrors,
  useProgramById,
  useUpdateProgram
} from "./programUtils";
import { UpdateProgramInput } from "../../API";
import {
  selectNetworks,
  selectNetworksStatus
} from "../Networks/networksSlice";
import { useUpdateProgramNetworks } from "../Networks/networkUtils";
import { EditProgramInputs } from "../../app/types";
import { useTrainingStages } from "../TrainingStages/trainingStagesAPI";

const pageDetails: PageWrapperProps = {
  className: "EditProgram",
  headerText: "Edit Program"
};

export const EditProgram = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUsersRoles = useAppSelector(selectUserRoles);
  const allNetworks = useAppSelector(selectNetworks);
  const allNetworksStatus = useAppSelector(selectNetworksStatus);
  const isSysAdmin = currentUsersRoles?.includes(Role.SystemAdmin);
  const {
    program,
    status: useProgramStatus,
    error: useProgramError
  } = useProgramById(id);
  const {
    institutions,
    status: useAllInstitutionsStatus,
    error: useAllInstitutionsError
  } = useAllInstitutions();
  const {
    updatedProgram,
    status: updateProgramStatus,
    error: updateProgramError,
    updateProgram
  } = useUpdateProgram();
  const {
    updateProgramNetworks,
    status: updateProgramNetworksStatus,
    error: updatePrograrmNetworksError
  } = useUpdateProgramNetworks();
  const {
    trainingStages,
    status: trainingStagesStatus
  } = useTrainingStages();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<EditProgramInputs>({
    defaultValues: {
      associatedNetworkIds: []
    },
    reValidateMode: "onChange"
  });

  const [formPopulated, setFormPopulated] = useState(false);
  const anyApiIsLoading = [
    useProgramStatus,
    useAllInstitutionsStatus,
    updateProgramStatus,
    allNetworksStatus,
    updateProgramNetworksStatus,
    trainingStagesStatus
  ].includes("loading");
  const statusOfRequiredInitialData = [
    useProgramStatus,
    useAllInstitutionsStatus,
    allNetworksStatus,
    trainingStagesStatus
  ];
  const requiredInitialDataIdle = statusOfRequiredInitialData.every(
    (status) => status === "idle"
  );
  const loadingRequiredInitialData =
    statusOfRequiredInitialData.includes("loading");
  const errorLoadingRequiredInitialData =
    statusOfRequiredInitialData.includes("failed");
  const loadedRequiredInitialData = statusOfRequiredInitialData.every(
    (status) => status === "succeeded"
  );

  const missingNeededInstitution = !isSysAdmin && !currentUser?.institutionId;

  useEffect(() => {
    // Criteria: program loading done and sponsor loading done
    if (loadedRequiredInitialData && program) {
      const { name, description, institutionId, networks } = program;
      const networkIds = networks?.map((network) => network?.id);
      const trainingStageId = (program as any).trainingStageId ?? null;

      reset({
        name,
        description,
        institutionId,
        trainingStageId,
        associatedNetworkIds: networkIds
      });
      setFormPopulated(true);
    }
  }, [loadedRequiredInitialData, program]);

  const onSubmit: SubmitHandler<EditProgramInputs> = async (data) => {
    try {
      if (id) {
        if (!data.trainingStageId) return;
        if (!data.associatedNetworkIds || data.associatedNetworkIds.length === 0) {
          setError("associatedNetworkIds", {
            type: "required",
            message: ProgramFormErrors.NoNetworks
          });
          return;
        }
        clearErrors("associatedNetworkIds");
        // Parse CreateProgramInput and send to createProgram
        const updateProgramInput: UpdateProgramInput & { trainingStageId: string } = {
          id: id,
          name: data.name,
          description: data.description,
          institutionId: data.institutionId,
          trainingStageId: data.trainingStageId,
          associatedNetworkIds: data.associatedNetworkIds
        };

        await updateProgram(updateProgramInput);
      }

      console.info("update succeeded");
    } catch (error) {
      console.error(
        "update failed with the following error",
        JSON.stringify(error, null, 2)
      );
      throw error;
    }
    navigate(`/programs/${id}`);
  };

  // No requests have yet been made to feth the data.
  if (requiredInitialDataIdle) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Awaiting data...")}</h2>
      </PageWrapper>
    );
  }

  // Loading of necessary data is in progress.
  if (loadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  // One of the necessary pieces of data experienced an error loading.
  if (errorLoadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error fetching program!")}</h2>
        <p>{useProgramError}</p>
      </PageWrapper>
    );
  }

  // Loaded the data, but the program doesn't exist.
  if (loadedRequiredInitialData && !program) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Program not found!")}</h2>
      </PageWrapper>
    );
  }

  // Loaded the data, have a valid program, but form hasn't been populated yet. Don't display, the form might display improperly
  if (loadedRequiredInitialData && program && !formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Populating form...")}</h2>
      </PageWrapper>
    );
  }

  // Loaded the data, have a valid program, and the form has been populated
  // with the necessary data. Show the form.
  if (loadedRequiredInitialData && program && formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <form
          className="NewProgramForm"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Grid spacing={2} direction="column">
            {missingNeededInstitution && (
              <Grid xs={8}>
                <Alert severity="warning">
                  {t(ProgramFormErrors.NoInstitution)}
                </Alert>
              </Grid>
            )}
            {isSysAdmin && (
              <div>
                <Grid container spacing={2}>
                  <Grid xs={8}>
                    <label htmlFor="institution-select">
                      {t("Institution")}
                    </label>
                    <select
                      required
                      disabled={anyApiIsLoading}
                      id="institution-select"
                      style={{ width: "100%" }}
                      {...register("institutionId")}
                    >
                      {institutions?.map((institution) => (
                        <option value={institution.id}>
                          {institution.name}
                        </option>
                      ))}
                    </select>
                  </Grid>
                  <Grid xs={8}>
                    <label htmlFor="networks-select">{t("Networks")}</label>
                    <br />
                    {allNetworks?.map((network, index) => (
                      <p>
                        {" "}
                        <label>
                          <input
                            key={index + network.id}
                            type="checkbox"
                            value={network.id}
                            disabled={anyApiIsLoading}
                            {...register(`associatedNetworkIds`)}
                          />{" "}
                          {`${network.name} - ${
                            network.sponsor?.name || "N/A"
                          } (Sponsor)`}
                        </label>
                      </p>
                    ))}
                    {errors.associatedNetworkIds && (
                      <Alert severity="error">
                        {t(ProgramFormErrors.NoNetworks)}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </div>
            )}
            <Grid xs={8}>
              <label htmlFor="training-stage-select">
                {t("Training Stage")}
              </label>
              <select
                required
                disabled={missingNeededInstitution || anyApiIsLoading}
                id="training-stage-select"
                style={{ width: "100%" }}
                {...register("trainingStageId", { required: true })}
              >
                <option value="" disabled>
                  {t("Select a training stage")}
                </option>
                {trainingStages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.displayName}
                  </option>
                ))}
              </select>
              {errors.trainingStageId && (
                <Alert severity="error">
                  {t("Please select a training stage.")}
                </Alert>
              )}
            </Grid>
            <Grid xs={8}>
              <label htmlFor="name">{t("Program Name")}</label>
              <input
                type="text"
                required
                placeholder={t("Program Name")}
                style={{ width: "100%" }}
                id="name"
                disabled={missingNeededInstitution || anyApiIsLoading}
                {...register("name", { required: true })}
              />
              {errors.name && (
                <Alert severity="error">
                  {t(ProgramFormErrors.EmptyProgramName)}
                </Alert>
              )}
            </Grid>
            <Grid xs={8}>
              <label htmlFor="description">{t("Program Description")}</label>
              <textarea
                placeholder={t("Program Description")}
                style={{ width: "100%" }}
                id="description"
                disabled={missingNeededInstitution || anyApiIsLoading}
                {...register("description")}
              />
            </Grid>
          </Grid>
          <button disabled={missingNeededInstitution || anyApiIsLoading}>
            {t("Save")}
          </button>
          <button
            disabled={anyApiIsLoading}
            onClick={() => navigate(`/programs/${id}`)}
          >
            {t("Cancel")}
          </button>
        </form>
      </PageWrapper>
    );
  }

  // Null return to satisfy typescript requirements
  return null;
};
